/**
 * Live availability of the cards on screen (T07-40, R21). The register keeps **one** subscription
 * to `inventory.changed:*` on inventory-service's stream (T03-17, scoped to this register's
 * location — the shop's quantity, not the web total) for the whole session: a new search never
 * reopens the connection (per-service streams cannot change topics in place). The payload arrives
 * at once, so a card sold on the web or held at another register closes its sell button within a
 * second, with the time it happened — no refetch needed for that; the hub's `pos:register:<id>`
 * (`variant availability_changed`) refetches the list in the background anyway.
 *
 * Offline rule (design/05 §4, T07-33): when the stream drops after it was live, one-of-a-kind
 * cards cannot be added from the list; when it is back they stay closed until the list was read
 * again (`resync` → search + quick buttons refetched). The server's hold is the final guard in any
 * case (409 on add). Items with a quantity (sealed, supplies) keep selling like the offline policy
 * says.
 */
import { inventoryTopic, type ChangeMessage, type TopicStatus } from "@tcg/realtime";
import { useRealtime } from "@tcg/realtime/react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useSyncExternalStore } from "react";

import { usePos } from "@/app/context";

export interface LiveAvailability {
  available: boolean;
  quantity: number;
  /** inventory-service's reason (`sold`, `reserved`, `live_show` …); null when available. */
  reason: string | null;
  /** When it changed (ISO — the event's `occurredAt`). */
  at: string;
  /** Local clock when the register heard it (compared with the list's read time). */
  receivedAt: number;
}

/** A read of the list this long after a change has it already (the refetch started after it). */
export const SUPERSEDED_AFTER_MS = 1_000;

export const ALL_INVENTORY_TOPIC = inventoryTopic("*") ?? "inventory.changed:*";

interface Payload {
  variantId?: unknown;
  available?: unknown;
  quantity?: unknown;
  reason?: unknown;
  occurredAt?: unknown;
}

export function availabilityOf(
  message: ChangeMessage,
  receivedAt: number,
): [string, LiveAvailability] | null {
  const p = (message.payload ?? {}) as Payload;
  const variantId = typeof p.variantId === "string" ? p.variantId : message.id;
  if (!variantId || typeof p.available !== "boolean") {
    return null;
  }
  return [
    variantId,
    {
      available: p.available,
      quantity: typeof p.quantity === "number" ? p.quantity : p.available ? 1 : 0,
      reason: typeof p.reason === "string" ? p.reason : null,
      at: typeof p.occurredAt === "string" ? p.occurredAt : message.at,
      receivedAt,
    },
  ];
}

export interface AvailabilitySnapshot {
  version: number;
  status: TopicStatus;
  everLive: boolean;
  /** The list is being read again after a resync. */
  rereading: boolean;
}

/** What the stream said per variant + the stream's state (one per tab, in `PosServices`). */
export class AvailabilityStore {
  private readonly entries = new Map<string, LiveAvailability>();
  private snapshot: AvailabilitySnapshot = {
    version: 0,
    status: "connecting",
    everLive: false,
    rereading: false,
  };
  private readonly listeners = new Set<() => void>();
  private readonly changeListeners = new Set<
    (variantId: string, entry: LiveAvailability) => void
  >();

  constructor(private readonly now: () => number = Date.now) {}

  /** Every change the stream says, as it arrives (the offline copy follows it — T07-42). */
  onChange(listener: (variantId: string, entry: LiveAvailability) => void): () => void {
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): AvailabilitySnapshot => this.snapshot;

  private emit(patch: Partial<AvailabilitySnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch, version: this.snapshot.version + 1 };
    for (const listener of [...this.listeners]) {
      listener();
    }
  }

  apply(message: ChangeMessage): void {
    const change = availabilityOf(message, this.now());
    if (change) {
      this.entries.set(change[0], change[1]);
      this.emit({});
      for (const listener of [...this.changeListeners]) {
        listener(change[0], change[1]);
      }
    }
  }

  setStatus(status: TopicStatus): void {
    if (status === this.snapshot.status) {
      return;
    }
    this.emit({ status, everLive: this.snapshot.everLive || status === "live" });
  }

  setRereading(rereading: boolean): void {
    if (rereading !== this.snapshot.rereading) {
      this.emit({ rereading });
    }
  }

  /** The latest change of `variantId` unless a read of the list at `readAt` already has it. */
  get(variantId: string, readAt: number): LiveAvailability | null {
    const entry = this.entries.get(variantId);
    if (!entry || readAt - entry.receivedAt > SUPERSEDED_AFTER_MS) {
      return null;
    }
    return entry;
  }

  /** One-of-a-kind cards wait: the stream dropped after it was live, or the list is re-read. */
  checking(): boolean {
    const s = this.snapshot;
    return s.everLive && (s.status !== "live" || s.rereading);
  }

  /** Drop what a fresh read of every list replaces. */
  clear(): void {
    if (this.entries.size > 0) {
      this.entries.clear();
      this.emit({});
    }
  }
}

/** Query roots the tiles come from — re-read after a resync before one-of-a-kind cards sell. */
export const AVAILABILITY_LISTS = [
  ["pos-search", "list"],
  ["pos-quick-items", "list"],
] as const;

/**
 * The register's single `inventory.changed:*` subscription (mounted once in the layout while the
 * register is signed in and its location is known).
 */
export function useAvailabilityFeed(enabled: boolean): TopicStatus {
  const { availability, realtimeScope } = usePos();
  const queryClient = useQueryClient();
  const scoped = Boolean(realtimeScope.locationId);
  const status = useRealtime([ALL_INVENTORY_TOPIC], (message) => availability.apply(message), {
    enabled: enabled && scoped,
    onResync: () => {
      // missed changes (replay impossible / back after a pause): read the lists again first
      availability.setRereading(true);
      void Promise.all(
        AVAILABILITY_LISTS.map((queryKey) =>
          queryClient.refetchQueries({ queryKey, type: "active" }),
        ),
      )
        .catch(() => undefined)
        .finally(() => {
          availability.clear();
          availability.setRereading(false);
        });
    },
  });
  const live = enabled && scoped ? status : "connecting";
  useEffect(() => {
    availability.setStatus(live);
  }, [availability, live]);
  return status;
}

/** Re-render when the stream says something (the tiles read {@link AvailabilityStore.get}). */
export function useAvailabilityStore(): AvailabilityStore {
  const { availability } = usePos();
  useSyncExternalStore(availability.subscribe, availability.getSnapshot, availability.getSnapshot);
  return availability;
}
